/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import GroupEditMixin from 'linced-app/mixins/group-edit';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import moment from 'moment';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class GroupsController extends Controller.extend(
  GroupEditMixin,
) {
  @service
  router;

  @action
  setYear(value) {
    this.year = value;
  }

  queryParams = ['year'];
  @tracked year = moment().year();

  // Services
  @service
  paperToaster;

  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  strings;

  @computed('session.scopes.[]')
  get isTenantAdmin() {
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  @alias('session.school.fields.core:groups:groupings.value')
  groupings;

  @tracked
  deleteGroupDialogOpen = false;
  @tracked
  deleteInProgress = false;
  @tracked currentYear = moment().year();
  @tracked nextYear = moment().add(1, 'year').year();
  prevYear = moment().subtract(1, 'year').year();

  // selectedTab is used by GroupEditMixin to determine which type of Group to create,
  // but is based on the index within the array of group types, so we have to find it.
  selectedTab = GroupType.values.indexOf(
    findBy(GroupType.values, 'value', GroupType.HOMEROOM),
  );

  // duplicationAvailable determines whether duplication from the previous year
  // is available.
  @computed('year', 'currentYear', 'model.groups.[]')
  get duplicationAvailable() {
    if (this.get('model.groups.length') > 0) {
      // Groups already exist; don't allow duplication.
      return false;
    }
    if (this.year === this.currentYear && moment().month() <= 1) {
      // It's currently January (0) or February (1); allow duplication.
      // This allows for those who didn't do it last year.
      return true;
    }

    // Allow duplication of this year's groups into next year.
    return this.year === this.nextYear;
  }

  // duplicationInProgress denotes whether duplication into a future year is in progress.
  @tracked
  duplicationInProgress = false;

  @tracked
  groupId;
  @tracked errorMessage;
  @tracked groupName;

  @computed('year', 'currentYear', 'nextYear', 'model.groups.length')
  get classPlacementsAvailable() {
    if (this.get('model.groups.length') > 0) {
      // There are groups to place students into.

      if (this.year === this.nextYear) {
        // Looking at next year, and we have groups there.
        return true;
      }
      if (this.year === this.currentYear && moment().month() <= 1) {
        // It's currently January (0) or February (1); allow placements.
        // This allows for those who didn't do their class placements last year.
        return true;
      }
    }
    return false;
  }

  // onDelete is called by the group-edit mixin upon deletion of a group.
  onDelete() {
    // Reset the edit form, to ensure a clean start if adding a new group.
    this.resetForm();
  }

  @action
  deleteGroupById(id) {
    this.errorMessage = '';
    this.store.findRecord('group', id).then((group) => {
      this.groupName = group.get('name');
    });
    this.groupId = id;
    this.deleteGroupDialogOpen = true;
  }

  @action
  confirmDeleteById(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let group = this.store.peekRecord('group', this.groupId);

        // Delete record (locally + pushed to server).
        return group
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            group.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteGroupDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  @action
  duplicateHomerooms() {
    this.duplicationInProgress = true;

    let schoolId = this.get('session.activeSchool');

    this.store
      .query('group', {
        schoolId,
        year: this.year - 1,
        groupType: GroupType.HOMEROOM,
      })
      .then((groups) => {
        let promises = groups.map(async (group) => {
          let newGroup = this.store.createRecord('group', {
            // A belongsTo takes a record, not the async proxy.
            schoolId: await group.schoolId,
            campusId: group.campusId,
            profileMedia: group.profileMedia,
            type: group.type,
            name: group.name,
            label: group.label,
            year: this.year,
            grouping: group.grouping,
            team: group.team,
            staff: JSON.parse(JSON.stringify(group.currentStaff)),
          });
          return newGroup.save();
        });
        Promise.allSettled(promises).then(() => {
          this.model.groups.update();
          this.duplicationInProgress = false;
          this.paperToaster.show(
            `${this.strings.homeroom} duplication complete.`,
            {
              duration: 4000,
              position: 'top right',
              toastClass: 'success',
            },
          );
        });
      });
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else if (!isEmpty(err.errors[0])) {
      let error = err.errors[0];
      if (!isEmpty(error.detail)) {
        this.errorMessage = error.detail;
      }
    } else {
      this.errorMessage = err.message;
    }
  }

  setErrorMessage(code, action) {
    var message = 'An unknown error occurred.  Please try again.';
    const codes = {
      400: 'Sorry, your request was invalid.  Please check all details below, and try again.',
      403: {
        create:
          'Sorry, you are not authorised to create a new user.  Please check that you are logged in as the correct user, and try again.',
        update:
          'Sorry, you are not authorised to edit this user.  Please check that you are logged in as the correct user, and try again.',
        delete:
          'Sorry, you are not authorised to delete this user.  Please check that you are logged in as the correct user, and try again.',
      },
      409: 'A user already exists with that username.  Please double-check the details, or find and update the existing user.',
    };

    if (!isEmpty(codes[code][action])) {
      message = codes[code][action];
    } else if (!isEmpty(codes[code])) {
      message = codes[code];
    }

    this.errorMessage = message;
  }

  // groupCreated is called by GroupEditMixin#saveGroup after record creation.
  @action
  groupCreated() {
    this.router.refresh('admin.groups');
  }
}
